import { useState, useEffect, useCallback } from "react";
import { api, formatApiError, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, FileSpreadsheet, ChevronDown, ChevronUp, Wallet, FileText, Receipt, CircleDollarSign, FolderKanban, FileSignature, Plus, RotateCcw, Pencil, Trash2, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { idr } from "../lib/format";
import { cn } from "../lib/utils";
import { useRealtime } from "../contexts/RealtimeContext";
import { useAuth } from "../contexts/AuthContext";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const EMPTY_MANUAL = { project_name: "", spk_number: "", client: "", month: new Date().getMonth() + 1, year: new Date().getFullYear(), contract_value: "", invoice_amount: "", paid_amount: "", outstanding: "", payment_date: "", notes: "" };

function ResetDialog({ years, onClose, onDone }) {
  const [scope, setScope] = useState("month");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(years[0] || new Date().getFullYear());
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/revenue/reset", { scope, year: scope === "all" ? null : year, month: scope === "month" ? month : null });
      toast.success(`Reset selesai — ${data.deleted} data manual dihapus (${scope === "month" ? `${MONTHS_ID[month - 1]} ${year}` : scope === "year" ? `tahun ${year}` : "semua"})`);
      onDone();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm bg-background" data-testid="revenue-reset-dialog">
        <DialogHeader><DialogTitle className="font-heading">Reset Data Omzet</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Reset menghapus <b>data manual entry</b> pada periode terpilih. Data tersinkron dari SPK/Invoice/Termin tidak pernah dihapus.</p>
          <div className="flex gap-1.5" data-testid="reset-scope-group">
            {[["month", "Per Bulan"], ["year", "Per Tahun"], ["all", "Semua"]].map(([k, label]) => (
              <button type="button" key={k} data-testid={`reset-scope-${k}`} onClick={() => setScope(k)}
                className={cn("flex-1 h-9 text-xs rounded-md transition-colors", scope === k ? "bg-[#E31E24] text-white font-medium" : "border border-border text-muted-foreground hover:bg-muted")}>
                {label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {scope === "month" && (
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="reset-month-select"
                className="h-9 px-2 text-sm rounded-md border border-border bg-transparent">
                {MONTHS_ID.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            )}
            {scope !== "all" && (
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="reset-year-select"
                className="h-9 px-2 text-sm rounded-md border border-border bg-transparent">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
          </div>
          <p className="text-xs text-[#E31E24] font-medium" data-testid="reset-warning">Tindakan ini tidak dapat dibatalkan.</p>
        </div>
        <DialogFooter>
          <button data-testid="reset-cancel" onClick={onClose} className="h-9 px-4 text-sm rounded-md border border-border hover:bg-muted transition-colors">Batal</button>
          <button data-testid="reset-confirm" onClick={submit} disabled={busy}
            className="h-9 px-4 text-sm rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white font-medium transition-colors disabled:opacity-60 flex items-center gap-1.5">
            {busy && <Loader2 size={13} className="animate-spin" />}Reset Sekarang
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualEntryDialog({ initial, years, onClose, onDone }) {
  const [form, setForm] = useState(initial ? { ...EMPTY_MANUAL, ...initial } : { ...EMPTY_MANUAL, year: years[0] || EMPTY_MANUAL.year });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const autoOutstanding = Math.max(0, (Number(form.contract_value) || 0) - (Number(form.paid_amount) || 0));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form, month: Number(form.month), year: Number(form.year),
        contract_value: Number(form.contract_value) || 0, invoice_amount: Number(form.invoice_amount) || 0,
        paid_amount: Number(form.paid_amount) || 0,
        outstanding: form.outstanding === "" ? null : Number(form.outstanding),
        payment_date: form.payment_date || null,
      };
      if (initial) await api.patch(`/revenue/manual/${initial.id}`, payload);
      else await api.post("/revenue/manual", payload);
      toast.success(initial ? "Manual entry diperbarui" : "Manual entry ditambahkan");
      onDone();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const inputCls = "w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary";
  const F = (k, label, props = {}) => (
    <div className="space-y-1" key={k}>
      <label className="text-[11px] font-medium text-muted-foreground">{label}</label>
      <input value={form[k]} onChange={set(k)} data-testid={`manual-field-${k.replace(/_/g, "-")}`} className={inputCls} {...props} />
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-background max-h-[90vh] overflow-y-auto" data-testid="manual-entry-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Edit Manual Entry" : "Manual Revenue Entry"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2.5">
            {F("project_name", "Proyek *", { placeholder: "Nama proyek" })}
            {F("spk_number", "No. SPK / Kontrak", { placeholder: "SPK 001/..." })}
            {F("client", "Klien", { placeholder: "PT / nama klien" })}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-muted-foreground">Bulan *</label>
                <select value={form.month} onChange={set("month")} data-testid="manual-field-month" className={inputCls}>
                  {MONTHS_ID.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-muted-foreground">Tahun *</label>
                <input type="number" min="2000" max="2100" value={form.year} onChange={set("year")} data-testid="manual-field-year" className={inputCls} />
              </div>
            </div>
            {F("contract_value", "Nilai Kontrak (IDR)", { type: "number", min: "0" })}
            {F("invoice_amount", "Nilai Invoice (IDR)", { type: "number", min: "0" })}
            {F("paid_amount", "Terbayar (IDR)", { type: "number", min: "0" })}
            <div className="space-y-1">
              <label className="text-[11px] font-medium text-muted-foreground">Outstanding (IDR)</label>
              <input type="number" min="0" value={form.outstanding} onChange={set("outstanding")} data-testid="manual-field-outstanding"
                placeholder={`Auto: ${idr(autoOutstanding)}`} className={inputCls} />
            </div>
            {F("payment_date", "Tanggal Pembayaran", { type: "date" })}
            {F("notes", "Catatan", { placeholder: "Opsional" })}
          </div>
          <p className="text-[11px] text-muted-foreground">Outstanding kosong = dihitung otomatis (Nilai Kontrak − Terbayar). Entry ditandai sebagai <b>Manual Entry</b> dan dipisahkan dari data tersinkron.</p>
          <DialogFooter>
            <button type="button" data-testid="manual-cancel" onClick={onClose} className="h-9 px-4 text-sm rounded-md border border-border hover:bg-muted transition-colors">Batal</button>
            <button type="submit" data-testid="manual-save" disabled={busy}
              className="h-9 px-4 text-sm rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white font-medium transition-colors disabled:opacity-60 flex items-center gap-1.5">
              {busy && <Loader2 size={13} className="animate-spin" />}Simpan
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const idrShort = (n) => {
  if (!n) return "Rp 0";
  if (n >= 1e9) return `Rp ${(n / 1e9).toFixed(1).replace(".", ",")} M`;
  if (n >= 1e6) return `Rp ${(n / 1e6).toFixed(1).replace(".", ",")} jt`;
  if (n >= 1e3) return `Rp ${Math.round(n / 1e3)} rb`;
  return `Rp ${n}`;
};

const SELECT_CLS = "h-8 px-2 text-xs rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary max-w-[150px]";

export default function Omzet() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [client, setClient] = useState("");
  const [projectId, setProjectId] = useState("");
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [manualDialog, setManualDialog] = useState(null); // "create" | entry object
  const [deletingManual, setDeletingManual] = useState(null);
  const { subscribe } = useRealtime();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (year) params.set("year", year);
    if (client) params.set("client", client);
    if (projectId) params.set("project_id", projectId);
    if (status) params.set("status", status);
    const { data } = await api.get(`/revenue/omzet?${params}`);
    setData(data);
    setYear((y) => y ?? data.year);
  }, [year, client, projectId, status]);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);

  useEffect(() => {
    let t;
    return subscribe(() => { clearTimeout(t); t = setTimeout(() => load().catch(() => {}), 800); });
  }, [subscribe, load]);

  const exportUrl = () => {
    const params = new URLSearchParams();
    if (year) params.set("year", year);
    if (client) params.set("client", client);
    if (projectId) params.set("project_id", projectId);
    if (status) params.set("status", status);
    return `${API_BASE}/revenue/export?${params}`;
  };

  const deleteManual = async (entry) => {
    setDeletingManual(entry.id);
    try {
      await api.delete(`/revenue/manual/${entry.id}`);
      toast.success("Manual entry dihapus");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeletingManual(null); }
  };

  const summaryCards = data ? [
    { key: "omzet", label: "Omzet Terbayar", value: idr(data.totals.paid), icon: CircleDollarSign, cls: "text-[#10B981]" },
    { key: "contract", label: "Nilai Kontrak", value: idr(data.totals.contract), icon: FileSignature, cls: "text-[#1B2A6B] dark:text-blue-300" },
    { key: "invoiced", label: "Total Invoice", value: idr(data.totals.invoiced), icon: FileText, cls: "text-blue-600 dark:text-blue-400" },
    { key: "outstanding", label: "Outstanding", value: idr(data.totals.outstanding), icon: Wallet, cls: "text-[#E31E24]" },
    { key: "projects", label: "Proyek", value: data.totals.project_count, icon: FolderKanban, cls: "text-foreground" },
    { key: "spk", label: "SPK Terbit", value: data.totals.spk_count, icon: Receipt, cls: "text-foreground" },
  ] : [];
  if (data && data.totals.manual_count > 0) {
    summaryCards.push({ key: "manual", label: "Manual Entry", value: idr(data.totals.manual_paid), icon: Pencil, cls: "text-amber-600 dark:text-amber-400" });
  }

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto" data-testid="omzet-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-md bg-[#10B981]/10 text-[#10B981] flex items-center justify-center"><TrendingUp size={17} /></span>
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight leading-tight">Omzet &amp; Revenue</h2>
            <p className="text-xs text-muted-foreground" data-testid="omzet-subtitle">Dihitung otomatis dari SPK, RAB, Invoice, Kuitansi &amp; Termin</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select value={year ?? ""} onChange={(e) => setYear(Number(e.target.value))} data-testid="omzet-year-select" className={SELECT_CLS}>
            {(data?.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {isAdmin && (
            <>
              <button data-testid="omzet-manual-add" onClick={() => setManualDialog("create")}
                className="h-8 px-3 flex items-center gap-1.5 text-xs font-medium rounded-md bg-[#1B2A6B] hover:bg-[#16224f] text-white transition-colors">
                <Plus size={13} />Manual Entry
              </button>
              <button data-testid="omzet-reset-button" onClick={() => setResetOpen(true)}
                className="h-8 px-3 flex items-center gap-1.5 text-xs font-medium rounded-md border border-[#E31E24]/40 text-[#E31E24] hover:bg-[#E31E24]/5 transition-colors">
                <RotateCcw size={13} />Reset
              </button>
            </>
          )}
          <a href={exportUrl()} download data-testid="omzet-export-button"
            className="h-8 px-3 flex items-center gap-1.5 text-xs font-medium rounded-md bg-[#10B981] hover:bg-[#0da271] text-white transition-colors">
            <FileSpreadsheet size={13} />Export Excel
          </a>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="omzet-filters">
          <select value={client} onChange={(e) => setClient(e.target.value)} data-testid="omzet-filter-client" className={SELECT_CLS}>
            <option value="">Semua Klien</option>
            {data.filters.clients.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="omzet-filter-project" className={SELECT_CLS}>
            <option value="">Semua Proyek</option>
            {data.filters.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="omzet-filter-status" className={SELECT_CLS}>
            <option value="">Semua Status</option>
            {data.filters.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {(client || projectId || status) && (
            <button data-testid="omzet-filter-reset" onClick={() => { setClient(""); setProjectId(""); setStatus(""); }}
              className="h-8 px-2.5 text-xs rounded-md border border-border text-muted-foreground hover:bg-muted transition-colors">
              Reset
            </button>
          )}
        </div>
      )}

      {data === null ? (
        <div className="flex justify-center py-20"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-5" data-testid="omzet-summary">
            {summaryCards.map((c) => (
              <div key={c.key} className="rounded-lg border border-border bg-card p-3" data-testid={`omzet-summary-${c.key}`}>
                <div className="flex items-center gap-1.5">
                  <c.icon size={12} className={c.cls} />
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</p>
                </div>
                <p className={cn("text-sm font-bold mt-1 truncate", c.cls)} title={String(c.value)}>{c.value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-border bg-card p-4 mb-5" data-testid="omzet-chart">
            <p className="text-sm font-semibold mb-3">Omzet Bulanan {data.year}</p>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={data.months} margin={{ top: 0, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => idrShort(v)} width={70} />
                <Tooltip formatter={(v, name) => [idr(v), { revenue: "Omzet (Terbayar)", invoiced: "Invoice", contract: "Kontrak Baru", manual_paid: "Manual Entry" }[name] || name]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend formatter={(v) => ({ revenue: "Omzet (Terbayar)", invoiced: "Invoice", contract: "Kontrak Baru", manual_paid: "Manual Entry" }[v] || v)} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" fill="#10B981" radius={[3, 3, 0, 0]} maxBarSize={26} />
                <Bar dataKey="invoiced" fill="#1B2A6B" radius={[3, 3, 0, 0]} maxBarSize={26} />
                <Bar dataKey="contract" fill="#94A3B8" radius={[3, 3, 0, 0]} maxBarSize={26} />
                <Bar dataKey="manual_paid" fill="#F59E0B" radius={[3, 3, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mb-5">
            <p className="text-sm font-semibold mb-2">Rincian Bulanan</p>
            <div className="space-y-1.5" data-testid="omzet-months">
              {data.months.map((m) => {
                const open = expanded === m.month;
                const share = data.totals.revenue ? Math.round((m.revenue / data.totals.revenue) * 100) : 0;
                return (
                  <div key={m.month} className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`omzet-month-row-${m.month}`}>
                    <button type="button" onClick={() => setExpanded(open ? null : m.month)} data-testid={`omzet-month-expand-${m.month}`}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors">
                      <span className="w-9 text-sm font-semibold shrink-0">{m.label}</span>
                      <span className="text-[11px] text-muted-foreground shrink-0">{m.project_count} proyek</span>
                      <div className="flex-1 min-w-0 hidden sm:flex items-center gap-3 justify-end">
                        <span className="text-xs text-muted-foreground">Invoice <span className="font-medium text-foreground">{idrShort(m.invoiced)}</span></span>
                        <span className="text-xs text-muted-foreground">Kontrak <span className="font-medium text-foreground">{idrShort(m.contract)}</span></span>
                      </div>
                      <span className="ml-auto sm:ml-0 text-sm font-bold text-[#10B981] shrink-0" data-testid={`omzet-month-amount-${m.month}`}>{idrShort(m.revenue)}</span>
                      {share > 0 && <span className="text-[10px] text-muted-foreground w-8 text-right shrink-0">{share}%</span>}
                      {open ? <ChevronUp size={14} className="text-muted-foreground shrink-0" /> : <ChevronDown size={14} className="text-muted-foreground shrink-0" />}
                    </button>
                    {open && (
                      <div className="border-t border-border px-3 py-2.5 space-y-2 bg-muted/20" data-testid={`omzet-month-detail-${m.month}`}>
                        {m.projects.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-1">Tidak ada aktivitas revenue bulan ini.</p>
                        ) : m.projects.map((p) => (
                          <div key={p.id} className="rounded-md border border-border bg-card p-2.5" data-testid={`omzet-month-${m.month}-project-${p.id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-xs font-semibold flex-1 min-w-0 truncate">{p.name}</p>
                              {p.manual && <span className="text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 bg-amber-500/15 text-amber-600 dark:text-amber-400 shrink-0" data-testid={`omzet-manual-badge-${p.id}`}>Manual</span>}
                              {p.client && <span className="text-[10px] text-muted-foreground truncate">{p.client}</span>}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-[11px] flex-wrap">
                              {p.paid > 0 && <span className="text-[#10B981] font-medium">Terbayar {idr(p.paid)}</span>}
                              {p.invoiced > 0 && <span className="text-blue-600 dark:text-blue-400">Invoice {idr(p.invoiced)}</span>}
                            </div>
                            {p.payments.length > 0 && (
                              <div className="mt-1.5 space-y-0.5">
                                {p.payments.map((pay, i) => (
                                  <p key={i} className="text-[10px] text-muted-foreground">
                                    {pay.date || "-"} · {pay.label} · <span className="font-medium text-foreground">{idr(pay.amount)}</span>
                                    {pay.kuitansi ? ` · ${pay.kuitansi}` : ""}{pay.method ? ` · ${pay.method}` : ""}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {(data.manual_entries || []).length > 0 && (
            <div className="mb-5" data-testid="omzet-manual-section">
              <p className="text-sm font-semibold mb-2 flex items-center gap-2 flex-wrap">
                Manual Entry
                <span className="text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 bg-amber-500/10 text-amber-600 dark:text-amber-400">{data.manual_entries.length} entri — terpisah dari data tersinkron</span>
              </p>
              <div className="space-y-1.5">
                {data.manual_entries.map((e) => (
                  <div key={e.id} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid={`manual-entry-row-${e.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 bg-amber-500/15 text-amber-600 dark:text-amber-400 shrink-0">Manual</span>
                      <p className="text-sm font-semibold flex-1 min-w-0 truncate">{e.project_name}</p>
                      <span className="text-[11px] text-muted-foreground shrink-0">{MONTHS_ID[e.month - 1]} {e.year}</span>
                      {isAdmin && (
                        <>
                          <button data-testid={`manual-edit-${e.id}`} onClick={() => setManualDialog(e)}
                            className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><Pencil size={12} />Edit</button>
                          <button data-testid={`manual-delete-${e.id}`} onClick={() => deleteManual(e)} disabled={deletingManual === e.id}
                            className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-[#E31E24]/10 text-muted-foreground hover:text-[#E31E24] transition-colors">
                            {deletingManual === e.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}Hapus
                          </button>
                        </>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 mt-2 text-[11px]">
                      <p className="text-muted-foreground">Kontrak</p><p className="text-right font-medium">{idr(e.contract_value)}</p>
                      <p className="text-muted-foreground">Invoice</p><p className="text-right">{idr(e.invoice_amount)}</p>
                      <p className="text-muted-foreground">Terbayar</p><p className="text-right text-[#10B981] font-medium">{idr(e.paid_amount)}</p>
                      <p className="text-muted-foreground">Outstanding</p><p className="text-right text-[#E31E24] font-medium">{idr(e.outstanding)}</p>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1.5 truncate">
                      {e.client || "-"}{e.spk_number ? ` · ${e.spk_number}` : ""}{e.payment_date ? ` · bayar ${e.payment_date}` : ""}{e.notes ? ` · ${e.notes}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-sm font-semibold mb-2">Revenue per Proyek</p>
            {data.projects.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="omzet-projects-empty">Belum ada proyek dengan nilai kontrak.</p>
            ) : (
              <>
                <div className="hidden md:block rounded-lg border border-border bg-card overflow-hidden" data-testid="omzet-projects-table">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50 text-muted-foreground text-left">
                        <th className="px-3 py-2 font-medium">Proyek</th>
                        <th className="px-3 py-2 font-medium">Klien</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium">No. SPK</th>
                        <th className="px-3 py-2 font-medium text-right">Nilai Kontrak</th>
                        <th className="px-3 py-2 font-medium text-right">Invoice</th>
                        <th className="px-3 py-2 font-medium text-right">Terbayar</th>
                        <th className="px-3 py-2 font-medium text-right">Outstanding</th>
                        <th className="px-3 py-2 font-medium">Bayar Terakhir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.projects.map((p) => (
                        <tr key={p.id} className="border-t border-border hover:bg-muted/30 transition-colors" data-testid={`omzet-project-row-${p.id}`}>
                          <td className="px-3 py-2 font-medium">{p.name}</td>
                          <td className="px-3 py-2 text-muted-foreground">{p.client || "-"}</td>
                          <td className="px-3 py-2"><span className="text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 bg-muted text-muted-foreground">{p.status}</span></td>
                          <td className="px-3 py-2 text-muted-foreground">{p.spk_number || "-"}</td>
                          <td className="px-3 py-2 text-right font-medium">{idr(p.contract_value)}</td>
                          <td className="px-3 py-2 text-right text-blue-600 dark:text-blue-400">{idr(p.invoiced)}</td>
                          <td className="px-3 py-2 text-right text-[#10B981] font-medium">{idr(p.paid)}</td>
                          <td className="px-3 py-2 text-right text-[#E31E24] font-medium">{idr(p.outstanding)}</td>
                          <td className="px-3 py-2 text-muted-foreground">{p.last_payment_date || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="md:hidden space-y-2" data-testid="omzet-projects-cards">
                  {data.projects.map((p) => (
                    <div key={p.id} className="rounded-lg border border-border bg-card p-3" data-testid={`omzet-project-card-${p.id}`}>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold flex-1 min-w-0 truncate">{p.name}</p>
                        <span className="text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 bg-muted text-muted-foreground shrink-0">{p.status}</span>
                      </div>
                      {p.client && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{p.client}</p>}
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px]">
                        <p className="text-muted-foreground">Kontrak</p><p className="text-right font-medium">{idr(p.contract_value)}</p>
                        <p className="text-muted-foreground">Invoice</p><p className="text-right text-blue-600 dark:text-blue-400">{idr(p.invoiced)}</p>
                        <p className="text-muted-foreground">Terbayar</p><p className="text-right text-[#10B981] font-medium">{idr(p.paid)}</p>
                        <p className="text-muted-foreground">Outstanding</p><p className="text-right text-[#E31E24] font-medium">{idr(p.outstanding)}</p>
                        {p.last_payment_date && (<><p className="text-muted-foreground">Bayar terakhir</p><p className="text-right">{p.last_payment_date}</p></>)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          {isAdmin && resetOpen && <ResetDialog years={data?.years || []} onClose={() => setResetOpen(false)} onDone={load} />}
          {isAdmin && manualDialog && (
            <ManualEntryDialog initial={manualDialog === "create" ? null : manualDialog} years={data?.years || []}
              onClose={() => setManualDialog(null)} onDone={load} />
          )}
        </>
      )}
    </div>
  );
}
